import type { ColumnsType } from 'antd/es/table';
import { Button, Space, Tag, Tooltip, Spin } from 'antd';
import { HistoryOutlined, SafetyOutlined, EditOutlined } from '@ant-design/icons';
import type { UserData } from './userColumns';
import type {
    PermissionBoard,
    PermissionStatus,
} from '../../hooks/useUserBoardPermissions';

const roleSortWeight: Record<string, number> = {
    ADMIN_MAIN: 1,
    ADVISOR: 2,
    SPECIAL: 3,
    REGULAR: 4,
    GENERAL: 5,
    STUDENT: 6,
    OTHER: 7,
};

const roleLabel = (role: string): string => {
    if (role === 'ADMIN_MAIN') return '관리자';
    if (role === 'ADVISOR') return '자문위원';
    if (role === 'REGULAR') return '정회원';
    if (role === 'STUDENT') return '학생회원';
    if (role === 'SPECIAL') return '특별회원';
    if (role === 'GENERAL') return '일반회원';
    if (role === 'OTHER') return '기타회원';
    return role;
};

const isAdminRole = (role: string) => role === 'ADMIN_MAIN' || role === 'ADMIN_SUB';

interface Options {
    showPermissions: boolean;
    permBoards: PermissionBoard[];
    statusByUser: Record<number, Record<number, PermissionStatus>>;
    permLoading: boolean;
    onGrant: (user: UserData) => void;
    onViewLogs: (user: UserData) => void;
    onEdit: (user: UserData) => void;
}

/**
 * 유저 권한 관리 탭 전용 컬럼.
 * showPermissions=true 이면 면허번호/전문의번호/휴대폰/가입일/활동 컬럼 대신
 * "게시판-카테고리 권한" 요약 컬럼을 노출한다.
 */
export const getUserPermissionColumns = ({
    showPermissions,
    permBoards,
    statusByUser,
    permLoading,
    onGrant,
    onViewLogs,
    onEdit,
}: Options): ColumnsType<UserData> => {
    const baseColumns: ColumnsType<UserData> = [
        {
            title: 'ID',
            dataIndex: 'username',
            key: 'username',
            width: 130,
            fixed: 'left',
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 100,
            fixed: 'left',
        },
        {
            title: 'Role',
            dataIndex: 'role',
            key: 'role',
            width: 90,
            defaultSortOrder: 'ascend',
            sorter: (a, b) =>
                (roleSortWeight[a.role] || 99) - (roleSortWeight[b.role] || 99),
            render: (role: string) => roleLabel(role),
        },
        {
            title: '소속',
            dataIndex: 'workplace',
            key: 'workplace',
            width: 160,
            ellipsis: { showTitle: false },
            render: (v: string) =>
                v ? <Tooltip title={v}>{v}</Tooltip> : <span style={{ color: '#bbb' }}>-</span>,
        },
        {
            title: '이메일',
            dataIndex: 'email',
            key: 'email',
            width: 180,
            ellipsis: { showTitle: false },
            render: (v: string) =>
                v ? <Tooltip title={v}>{v}</Tooltip> : <span style={{ color: '#bbb' }}>-</span>,
        },
    ];

    const detailColumns: ColumnsType<UserData> = [
        {
            title: '면허번호',
            dataIndex: 'medicalLicenseNumber',
            key: 'medicalLicenseNumber',
            width: 90,
            render: (v: string | null) => v || <span style={{ color: '#bbb' }}>없음</span>,
        },
        {
            title: '전문의번호',
            dataIndex: 'specialistNumber',
            key: 'specialistNumber',
            width: 90,
            render: (v: string | null) => v || <span style={{ color: '#bbb' }}>없음</span>,
        },
        {
            title: '휴대폰',
            dataIndex: 'phone',
            key: 'phone',
            width: 110,
            render: (v: string) => v || <span style={{ color: '#bbb' }}>-</span>,
        },
        {
            title: '가입일',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 120,
            render: (dateString: string) =>
                dateString ? new Date(dateString).toLocaleDateString() : '-',
        },
        {
            title: '활동',
            key: 'logs',
            align: 'center',
            width: 120,
            render: (_, record) =>
                !record.isWithdrawnYN && (
                    <Space>
                        <Button
                            type="link"
                            icon={<HistoryOutlined />}
                            onClick={() => onViewLogs(record)}
                            style={{ padding: 4 }}
                        >
                            조회 이력
                        </Button>
                    </Space>
                ),
        },
    ];

    const permissionColumn: ColumnsType<UserData> = [
        {
            title: '게시판-카테고리 권한',
            key: 'boardPermissions',
            render: (_, record) => {
                if (isAdminRole(record.role)) {
                    return <Tag color="gold">관리자 · 전체 권한</Tag>;
                }
                if (permLoading) {
                    return <Spin size="small" />;
                }

                const userStatus = statusByUser[record.userId] || {};
                const tags = permBoards
                    .map((board) => {
                        const status = userStatus[board.boardId] || 'NONE';
                        if (status === 'NONE') return null;
                        return (
                            <Tag
                                key={board.boardId}
                                color={status === 'ALL' ? 'blue' : 'green'}
                                style={{ margin: 0 }}
                            >
                                {board.label} · {status === 'ALL' ? '전체' : '부분'}
                            </Tag>
                        );
                    })
                    .filter(Boolean);

                if (tags.length === 0) {
                    return <span style={{ color: '#bbb' }}>권한 없음</span>;
                }

                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{tags}</div>
                );
            },
        },
    ];

    const actionColumn: ColumnsType<UserData> = [
        {
            title: '',
            key: 'action',
            align: 'center',
            width: 120,
            fixed: 'right',
            render: (_, record) => {
                if (record.isWithdrawnYN) return null;

                // 게시판 권한 보기 OFF → 유저 정보 수정 / ON → 게시판-카테고리 권한 부여
                if (!showPermissions) {
                    return (
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => onEdit(record)}
                        >
                            수정
                        </Button>
                    );
                }

                if (isAdminRole(record.role)) return null;
                return (
                    <Button
                        type="primary"
                        size="small"
                        icon={<SafetyOutlined />}
                        onClick={() => onGrant(record)}
                    >
                        권한 부여
                    </Button>
                );
            },
        },
    ];

    return [
        ...baseColumns,
        ...(showPermissions ? permissionColumn : detailColumns),
        ...actionColumn,
    ];
};
